/**
 * SelectAllBar - Top bar with select all, status, and actions
 */

import { useCallback, useState } from "react";
import { Plus, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { TAXONOMY_COLORS } from "@/components/audio/audioTypes";
import type { SceneWithShots } from "@/types/shots";
import "./SelectAllBar.css";

interface SelectAllBarProps {
  totalCount: number;
  selectedCount: number;
  readyCount: number;
  processingCount: number;
  isAllSelected: boolean;
  scenes: SceneWithShots[];
  onSelectAllToggle: () => void;
  onAddCategory: (category: string) => void;
  onSetScene: (sceneId: string) => void;
  onAddMore: () => void;
}

export function SelectAllBar({
  selectedCount,
  readyCount,
  processingCount,
  isAllSelected,
  scenes,
  onSelectAllToggle,
  onAddCategory,
  onSetScene,
  onAddMore,
}: SelectAllBarProps) {
  const [categoryInput, setCategoryInput] = useState("");

  const handleAddCategory = useCallback(() => {
    const trimmed = categoryInput.trim();
    if (trimmed) {
      onAddCategory(trimmed);
      setCategoryInput("");
    }
  }, [categoryInput, onAddCategory]);

  const handleCategoryKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddCategory();
      }
    },
    [handleAddCategory],
  );

  return (
    <div
      className={cn(
        "select-all-bar",
        selectedCount > 0 && "select-all-bar--selected",
      )}
    >
      {/* Left side - checkbox and status */}
      <div className="select-all-bar__left">
        <Checkbox checked={isAllSelected} onCheckedChange={onSelectAllToggle} />
        <span className="select-all-bar__label">Select all</span>
        <span className="select-all-bar__status">
          {readyCount} ready
          {processingCount > 0 && ` · ${processingCount} processing`}
        </span>
      </div>

      {/* Right side - category dropdown and add more */}
      <div className="select-all-bar__right">
        {/* Set scene dropdown (only visible when items selected) */}
        {selectedCount > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="select-all-bar__category-trigger"
                style={
                  {
                    "--trigger-color": TAXONOMY_COLORS.scene.hex,
                    "--trigger-color-rgb": TAXONOMY_COLORS.scene.rgb,
                  } as React.CSSProperties
                }
              >
                <span className="select-all-bar__trigger-dot" />
                <span>Set scene</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {scenes.length > 0 ? (
                scenes.map((scene) => (
                  <DropdownMenuItem
                    key={scene.id}
                    onClick={() => onSetScene(scene.id)}
                  >
                    {scene.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground">No scenes</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Set category dropdown (only visible when items selected) */}
        {selectedCount > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="select-all-bar__category-trigger"
                style={
                  {
                    "--trigger-color": TAXONOMY_COLORS.category.hex,
                    "--trigger-color-rgb": TAXONOMY_COLORS.category.rgb,
                  } as React.CSSProperties
                }
              >
                <span className="select-all-bar__trigger-dot" />
                <span>Set category</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 p-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyDown={handleCategoryKeyDown}
                  placeholder="Enter category..."
                  className="select-all-bar__category-input"
                  autoFocus
                />
                {categoryInput.trim() && (
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="select-all-bar__category-add"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Add more button */}
        <button
          type="button"
          data-testid="upload-add-button"
          onClick={onAddMore}
          className="select-all-bar__add-more"
        >
          <Plus className="w-4 h-4" />
          Add more
        </button>
      </div>
    </div>
  );
}
